import React from "react";
import { api } from "./api";

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatCents(cents: number): string {
  const dollars = Math.abs(cents) / 100;
  const sign = cents < 0 ? "-" : "";
  return `${sign}$${dollars.toFixed(2)}`;
}

function formatDate(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function navigate(hash: string) {
  window.location.hash = hash;
}

function parseHash(): { page: string; id?: string } {
  const raw = window.location.hash.replace(/^#\/?/, "");
  const parts = raw.split("/");
  return { page: parts[0] || "dashboard", id: parts[1] };
}

// ── Shared Components ──────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const label = (status || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return <span className={`badge badge-${status}`}>{label}</span>;
}

function ConfirmDialog({
  message,
  onConfirm,
  onCancel,
}: {
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h3>Confirm Action</h3>
        <p>{message}</p>
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button className="btn btn-danger" onClick={onConfirm}>
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

function Pagination({
  page,
  total,
  perPage,
  onPageChange,
}: {
  page: number;
  total: number;
  perPage: number;
  onPageChange: (p: number) => void;
}) {
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  if (totalPages <= 1) return null;
  return (
    <div className="pagination">
      <button
        className="btn btn-secondary btn-sm"
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
      >
        Previous
      </button>
      <span className="pagination-info">
        Page {page} of {totalPages}
      </span>
      <button
        className="btn btn-secondary btn-sm"
        disabled={page >= totalPages}
        onClick={() => onPageChange(page + 1)}
      >
        Next
      </button>
    </div>
  );
}

function NotesList({
  entityType,
  entityId,
  notes,
  onRefresh,
}: {
  entityType: string;
  entityId: number;
  notes: any[];
  onRefresh: () => void;
}) {
  const [content, setContent] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  async function addNote() {
    if (!content.trim()) return;
    setSaving(true);
    try {
      await api.post("/notes", {
        entity_type: entityType,
        entity_id: entityId,
        content: content.trim(),
      });
      setContent("");
      onRefresh();
    } catch {
      // silent
    }
    setSaving(false);
  }

  async function deleteNote(id: number) {
    try {
      await api.del(`/notes/${id}`);
      onRefresh();
    } catch {
      // silent
    }
  }

  return (
    <div>
      {notes.length === 0 && (
        <p className="empty-state" style={{ padding: "20px" }}>
          No notes yet.
        </p>
      )}
      {notes.map((n: any) => (
        <div className="note-item" key={n.note_id}>
          <div className="note-date">{formatDate(n.created_at)}</div>
          <div className="note-content">{n.content}</div>
          <button
            className="note-delete"
            title="Delete note"
            onClick={() => deleteNote(n.note_id)}
          >
            X
          </button>
        </div>
      ))}
      <div style={{ marginTop: 12 }}>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Add a note..."
        />
        <button
          className="btn btn-primary"
          style={{ marginTop: 8 }}
          disabled={saving || !content.trim()}
          onClick={addNote}
        >
          {saving ? "Saving..." : "Add Note"}
        </button>
      </div>
    </div>
  );
}

// ── Dashboard ──────────────────────────────────────────────────────────────────

function Dashboard() {
  const [stats, setStats] = React.useState<any>(null);
  const [recentSubs, setRecentSubs] = React.useState<any[]>([]);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    api
      .get("/dashboard/stats")
      .then(setStats)
      .catch((e) => setError(e.message));
    api
      .get("/submissions?page=1&per_page=5")
      .then((r) => setRecentSubs(r.data || []))
      .catch(() => {});
  }, []);

  if (error) return <div className="error-msg">{error}</div>;
  if (!stats) {
    return (
      <div className="loading">
        <div className="loading-spinner" />
        <div>Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div>
      <h1>Dashboard</h1>

      <div className="stat-grid">
        <div className="stat-card">
          <span className="stat-number">{stats.submissions?.new ?? 0}</span>
          <span className="stat-label">New Submissions</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">{stats.jobs?.active ?? 0}</span>
          <span className="stat-label">Active Jobs</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">{stats.invoices?.unpaid ?? 0}</span>
          <span className="stat-label">Unpaid Invoices</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">
            {formatCents(stats.invoices?.total_outstanding_cents ?? 0)}
          </span>
          <span className="stat-label">Total Outstanding</span>
        </div>
      </div>

      <div className="card">
        <h3>Recent Submissions</h3>
        {recentSubs.length === 0 ? (
          <p className="empty-state">No submissions yet.</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Name</th>
                  <th>Service</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {recentSubs.map((s) => (
                  <tr
                    key={s.id}
                    className="table-row"
                    onClick={() =>
                      navigate(`#/submissions/${s.id}`)
                    }
                  >
                    <td>{formatDate(s.created_at)}</td>
                    <td>
                      {s.first_name} {s.last_name}
                    </td>
                    <td>{s.service_type || "—"}</td>
                    <td>
                      <StatusBadge status={s.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <h3>Quick Links</h3>
      <div className="quick-links">
        <div className="quick-link" onClick={() => navigate("#/submissions")}>
          Submissions
        </div>
        <div className="quick-link" onClick={() => navigate("#/clients")}>
          Clients
        </div>
        <div className="quick-link" onClick={() => navigate("#/jobs")}>
          Jobs
        </div>
        <div className="quick-link" onClick={() => navigate("#/invoices")}>
          Invoices
        </div>
      </div>
    </div>
  );
}

// ── Submissions List ───────────────────────────────────────────────────────────

function SubmissionsList() {
  const [subs, setSubs] = React.useState<any[]>([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [status, setStatus] = React.useState("all");
  const [loading, setLoading] = React.useState(true);
  const perPage = 20;

  React.useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ page: String(page) });
    if (status !== "all") params.set("status", status);
    api
      .get(`/submissions?${params}`)
      .then((r) => {
        setSubs(r.data || []);
        setTotal(r.total || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, status]);

  const tabs = ["all", "new", "reviewed", "converted", "archived"];

  return (
    <div>
      <h1>Submissions</h1>

      <div className="filter-tabs">
        {tabs.map((t) => (
          <button
            key={t}
            className={`filter-tab ${status === t ? "active" : ""}`}
            onClick={() => {
              setStatus(t);
              setPage(1);
            }}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="loading">
          <div className="loading-spinner" />
          <div>Loading...</div>
        </div>
      ) : subs.length === 0 ? (
        <div className="empty-state">No submissions found.</div>
      ) : (
        <>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Name</th>
                  <th>Service</th>
                  <th>Status</th>
                  <th>Email</th>
                </tr>
              </thead>
              <tbody>
                {subs.map((s) => (
                  <tr
                    key={s.id}
                    className="table-row"
                    onClick={() =>
                      navigate(`#/submissions/${s.id}`)
                    }
                  >
                    <td>{formatDate(s.created_at)}</td>
                    <td>
                      {s.first_name} {s.last_name}
                    </td>
                    <td>{s.service_type || "—"}</td>
                    <td>
                      <StatusBadge status={s.status} />
                    </td>
                    <td>{s.email || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={page}
            total={total}
            perPage={perPage}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
}

// ── Submission Detail ──────────────────────────────────────────────────────────

function SubmissionDetail({ id }: { id: string }) {
  const [sub, setSub] = React.useState<any>(null);
  const [error, setError] = React.useState("");
  const [updating, setUpdating] = React.useState(false);

  // Create Job form state
  const [showJobForm, setShowJobForm] = React.useState(false);
  const [jobTitle, setJobTitle] = React.useState("");
  const [jobDesc, setJobDesc] = React.useState("");
  const [jobServiceType, setJobServiceType] = React.useState("");
  const [clientMode, setClientMode] = React.useState<"new" | "existing">("new");
  const [selectedClientId, setSelectedClientId] = React.useState<number | null>(null);
  const [clients, setClients] = React.useState<any[]>([]);
  const [creatingJob, setCreatingJob] = React.useState(false);

  function load() {
    api
      .get(`/submissions/${id}`)
      .then(setSub)
      .catch((e) => setError(e.message));
  }

  React.useEffect(load, [id]);

  async function changeStatus(newStatus: string) {
    setUpdating(true);
    try {
      await api.put(`/submissions/${id}/status`, { status: newStatus });
      load();
    } catch (e: any) {
      setError(e.message);
    }
    setUpdating(false);
  }

  async function convert() {
    setUpdating(true);
    try {
      const result = await api.post(`/submissions/${id}/convert`);
      if (result.client_id) {
        navigate(`#/clients/${result.client_id}`);
      } else {
        load();
      }
    } catch (e: any) {
      setError(e.message);
    }
    setUpdating(false);
  }

  const SERVICE_LABELS: Record<string, string> = {
    repair: "Repair",
    install: "Installation",
    maintenance: "Maintenance",
    other: "Service",
  };

  function openJobForm() {
    const label = SERVICE_LABELS[sub.service_type] || "Service";
    setJobTitle(`${label} - ${sub.first_name} ${sub.last_name}`);
    setJobDesc(sub.description || "");
    setJobServiceType(sub.service_type || "repair");

    if (sub.status === "converted") {
      api
        .get("/clients?per_page=200")
        .then((res: any) => {
          const allClients = res.data || [];
          setClients(allClients);
          const linked = allClients.find(
            (c: any) => c.source_submission_id === sub.id
          );
          if (linked) {
            setClientMode("existing");
            setSelectedClientId(linked.id);
          } else {
            setClientMode("new");
            setSelectedClientId(null);
          }
        })
        .catch(() => {});
    } else {
      setClientMode("new");
      setSelectedClientId(null);
    }

    setShowJobForm(true);
  }

  function closeJobForm() {
    setShowJobForm(false);
  }

  async function loadClients() {
    try {
      const res = await api.get("/clients?per_page=200");
      setClients(res.data || []);
    } catch {}
  }

  async function saveJob() {
    setCreatingJob(true);
    try {
      const body: any = {
        title: jobTitle,
        description: jobDesc,
        service_type: jobServiceType,
      };
      if (clientMode === "existing" && selectedClientId) {
        body.client_id = selectedClientId;
      }
      const result = await api.post(`/submissions/${id}/create-job`, body);
      if (result.job_id) {
        navigate(`#/jobs/${result.job_id}`);
      }
    } catch (e: any) {
      setError(e.message);
    }
    setCreatingJob(false);
  }

  if (error) return <div className="error-msg">{error}</div>;
  if (!sub) {
    return (
      <div className="loading">
        <div className="loading-spinner" />
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="breadcrumbs">
        <a href="#" onClick={() => navigate("#/submissions")}>
          Submissions
        </a>
        <span className="separator">&gt;</span>
        <span className="current">
          {sub.first_name} {sub.last_name}
        </span>
      </div>

      <div className="page-header">
        <h1>
          {sub.first_name} {sub.last_name}
        </h1>
        <StatusBadge status={sub.status} />
      </div>

      <div className="card">
        <div className="detail-grid">
          <div className="detail-field">
            <div className="field-label">Email</div>
            <div className="field-value">{sub.email || "—"}</div>
          </div>
          <div className="detail-field">
            <div className="field-label">Phone</div>
            <div className="field-value">{sub.phone || "—"}</div>
          </div>
          <div className="detail-field">
            <div className="field-label">Service Type</div>
            <div className="field-value">{sub.service_type || "—"}</div>
          </div>
          <div className="detail-field">
            <div className="field-label">Submitted</div>
            <div className="field-value">{formatDate(sub.created_at)}</div>
          </div>
          {sub.business_name && (
            <div className="detail-field">
              <div className="field-label">Business</div>
              <div className="field-value">{sub.business_name}</div>
            </div>
          )}
          {sub.referral_source && (
            <div className="detail-field">
              <div className="field-label">Referral Source</div>
              <div className="field-value">{sub.referral_source}</div>
            </div>
          )}
        </div>
        {sub.description && (
          <div className="detail-field" style={{ marginTop: 12 }}>
            <div className="field-label">Description</div>
            <div
              className="field-value"
              style={{ whiteSpace: "pre-wrap" }}
            >
              {sub.description}
            </div>
          </div>
        )}
      </div>

      <div className="card">
        <h3>Update Status</h3>
        <div className="form-group">
          <select
            value={sub.status}
            onChange={(e) => changeStatus(e.target.value)}
            disabled={updating}
          >
            <option value="new">New</option>
            <option value="reviewed">Reviewed</option>
            <option value="converted">Converted</option>
            <option value="archived">Archived</option>
          </select>
        </div>

        {sub.status !== "converted" && (
          <button
            className="btn btn-primary"
            onClick={convert}
            disabled={updating}
            style={{ marginTop: 8 }}
          >
            Create Client from Submission
          </button>
        )}

        {!showJobForm && (
          <button
            className="btn"
            onClick={openJobForm}
            disabled={updating}
            style={{
              marginTop: 8,
              marginLeft: sub.status !== "converted" ? 8 : 0,
              background: "#d97706",
              color: "#fff",
              fontWeight: 700,
              fontSize: 15,
              padding: "10px 24px",
              border: "none",
              borderRadius: 6,
              cursor: "pointer",
            }}
          >
            CREATE JOB
          </button>
        )}
      </div>

      {showJobForm && (
        <div className="card" style={{ borderLeft: "4px solid #d97706" }}>
          <h3 style={{ marginTop: 0 }}>Create Job from This Submission</h3>

          <div className="form-group">
            <label style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>
              Job Title
            </label>
            <input
              type="text"
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              style={{ width: "100%", padding: 8, fontSize: 15 }}
            />
          </div>

          <div className="form-group">
            <label style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>
              Description
            </label>
            <textarea
              value={jobDesc}
              onChange={(e) => setJobDesc(e.target.value)}
              rows={3}
              style={{ width: "100%", padding: 8, fontSize: 15 }}
            />
          </div>

          <div className="form-group">
            <label style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>
              Service Type
            </label>
            <select
              value={jobServiceType}
              onChange={(e) => setJobServiceType(e.target.value)}
              style={{ padding: 8, fontSize: 15 }}
            >
              <option value="repair">Repair</option>
              <option value="install">Installation</option>
              <option value="maintenance">Maintenance</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div style={{ margin: "16px 0 8px", fontWeight: 600, borderTop: "1px solid #ddd", paddingTop: 12 }}>
            Link to Client
          </div>

          <div className="form-group">
            <label style={{ display: "block", marginBottom: 8, cursor: "pointer", fontSize: 15 }}>
              <input
                type="radio"
                name="clientMode"
                checked={clientMode === "new"}
                onChange={() => setClientMode("new")}
                style={{ marginRight: 8 }}
              />
              Create new client from this submission
            </label>
            <label style={{ display: "block", cursor: "pointer", fontSize: 15 }}>
              <input
                type="radio"
                name="clientMode"
                checked={clientMode === "existing"}
                onChange={() => {
                  setClientMode("existing");
                  if (clients.length === 0) loadClients();
                }}
                style={{ marginRight: 8 }}
              />
              Link to existing client
            </label>

            {clientMode === "existing" && (
              <select
                value={selectedClientId ?? ""}
                onChange={(e) =>
                  setSelectedClientId(
                    e.target.value ? Number(e.target.value) : null
                  )
                }
                style={{ marginTop: 8, padding: 8, fontSize: 15, width: "100%" }}
              >
                <option value="">-- Select a client --</option>
                {clients.map((c: any) => (
                  <option key={c.id} value={c.id}>
                    {c.first_name} {c.last_name}
                    {c.business_name ? ` (${c.business_name})` : ""}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
            <button
              className="btn"
              onClick={saveJob}
              disabled={creatingJob || (clientMode === "existing" && !selectedClientId)}
              style={{
                background: "#d97706",
                color: "#fff",
                fontWeight: 700,
                fontSize: 15,
                padding: "10px 24px",
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
                opacity:
                  creatingJob || (clientMode === "existing" && !selectedClientId)
                    ? 0.5
                    : 1,
              }}
            >
              {creatingJob ? "Saving..." : "SAVE JOB"}
            </button>
            <button
              className="btn"
              onClick={closeJobForm}
              disabled={creatingJob}
              style={{ padding: "10px 24px", fontSize: 15 }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="section-header">
        <h3>Notes</h3>
      </div>
      <NotesList
        entityType="submission"
        entityId={sub.id}
        notes={sub.notes || []}
        onRefresh={load}
      />

      {sub.attachments && sub.attachments.length > 0 && (
        <>
          <div className="section-header">
            <h3>Attachments</h3>
          </div>
          <div className="attachment-grid">
            {sub.attachments.map((att: any) => (
              <a
                key={att.id}
                href={`/admin/api/attachments/${att.id}/download`}
                target="_blank"
                rel="noopener noreferrer"
                className="attachment-card"
              >
                {att.mime_type.startsWith("image/") ? (
                  <img
                    src={`/admin/api/attachments/${att.id}/download`}
                    alt={att.original_name}
                    className="attachment-thumb"
                  />
                ) : (
                  <div className="attachment-icon">PDF</div>
                )}
                <div className="attachment-info">
                  <div className="attachment-name">{att.original_name}</div>
                  <div className="attachment-size">
                    {att.size_bytes < 1024 * 1024
                      ? (att.size_bytes / 1024).toFixed(1) + " KB"
                      : (att.size_bytes / (1024 * 1024)).toFixed(1) + " MB"}
                  </div>
                </div>
              </a>
            ))}
          </div>
        </>
      )}

      <div style={{ marginTop: 24 }}>
        <button
          className="btn btn-secondary"
          onClick={() => navigate("#/submissions")}
        >
          Back to Submissions
        </button>
      </div>
    </div>
  );
}

// ── Clients List ───────────────────────────────────────────────────────────────

function ClientsList() {
  const [clients, setClients] = React.useState<any[]>([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [loading, setLoading] = React.useState(true);
  const [showAdd, setShowAdd] = React.useState(false);
  const [form, setForm] = React.useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    business_name: "",
  });
  const perPage = 20;

  function load() {
    setLoading(true);
    api
      .get(`/clients?page=${page}`)
      .then((r) => {
        setClients(r.data || []);
        setTotal(r.total || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  React.useEffect(load, [page]);

  async function addClient() {
    if (!form.first_name.trim() || !form.last_name.trim()) return;
    try {
      await api.post("/clients", form);
      setShowAdd(false);
      setForm({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        business_name: "",
      });
      load();
    } catch {
      // silent
    }
  }

  return (
    <div>
      <div className="page-header">
        <h1>Clients</h1>
        <button
          className="btn btn-primary"
          onClick={() => setShowAdd(!showAdd)}
        >
          {showAdd ? "Cancel" : "Add Client"}
        </button>
      </div>

      {showAdd && (
        <div className="card">
          <h3>New Client</h3>
          <div className="inline-form">
            <div className="form-group">
              <label className="form-label">First Name *</label>
              <input
                type="text"
                value={form.first_name}
                onChange={(e) =>
                  setForm({ ...form, first_name: e.target.value })
                }
              />
            </div>
            <div className="form-group">
              <label className="form-label">Last Name *</label>
              <input
                type="text"
                value={form.last_name}
                onChange={(e) =>
                  setForm({ ...form, last_name: e.target.value })
                }
              />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) =>
                  setForm({ ...form, email: e.target.value })
                }
              />
            </div>
            <div className="form-group">
              <label className="form-label">Phone</label>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) =>
                  setForm({ ...form, phone: e.target.value })
                }
              />
            </div>
            <div className="form-group form-group-full">
              <label className="form-label">Business Name</label>
              <input
                type="text"
                value={form.business_name}
                onChange={(e) =>
                  setForm({ ...form, business_name: e.target.value })
                }
              />
            </div>
          </div>
          <button
            className="btn btn-primary"
            style={{ marginTop: 12 }}
            onClick={addClient}
          >
            Save Client
          </button>
        </div>
      )}

      {loading ? (
        <div className="loading">
          <div className="loading-spinner" />
          <div>Loading...</div>
        </div>
      ) : clients.length === 0 ? (
        <div className="empty-state">No clients found.</div>
      ) : (
        <>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Business</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((c) => (
                  <tr
                    key={c.id}
                    className="table-row"
                    onClick={() => navigate(`#/clients/${c.id}`)}
                  >
                    <td>
                      {c.first_name} {c.last_name}
                    </td>
                    <td>{c.business_name || "—"}</td>
                    <td>{c.phone || "—"}</td>
                    <td>{c.email || "—"}</td>
                    <td>{formatDate(c.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={page}
            total={total}
            perPage={perPage}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
}

// ── Client Detail ──────────────────────────────────────────────────────────────

function ClientDetail({ id }: { id: string }) {
  const [client, setClient] = React.useState<any>(null);
  const [error, setError] = React.useState("");
  const [editing, setEditing] = React.useState(false);
  const [editForm, setEditForm] = React.useState<any>({});
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [showJobForm, setShowJobForm] = React.useState(false);
  const [jobForm, setJobForm] = React.useState({
    title: "",
    description: "",
    service_type: "",
  });
  const [showInvoiceCreate, setShowInvoiceCreate] = React.useState(false);
  const [invoiceForm, setInvoiceForm] = React.useState({
    job_id: "",
    notes: "",
    due_date: "",
  });

  function load() {
    api
      .get(`/clients/${id}`)
      .then((c) => {
        setClient(c);
        setEditForm({
          first_name: c.first_name || "",
          last_name: c.last_name || "",
          email: c.email || "",
          phone: c.phone || "",
          business_name: c.business_name || "",
          address: c.address || "",
        });
      })
      .catch((e) => setError(e.message));
  }

  React.useEffect(load, [id]);

  async function saveEdit() {
    try {
      await api.put(`/clients/${id}`, editForm);
      setEditing(false);
      load();
    } catch (e: any) {
      setError(e.message);
    }
  }

  async function deleteClient() {
    try {
      await api.del(`/clients/${id}`);
      navigate("#/clients");
    } catch (e: any) {
      setError(e.message);
    }
    setConfirmDelete(false);
  }

  async function createJob() {
    if (!jobForm.title.trim()) return;
    try {
      const result = await api.post("/jobs", {
        client_id: Number(id),
        ...jobForm,
      });
      setShowJobForm(false);
      setJobForm({ title: "", description: "", service_type: "" });
      if (result.id) {
        navigate(`#/jobs/${result.id}`);
      } else {
        load();
      }
    } catch (e: any) {
      setError(e.message);
    }
  }

  async function createInvoice() {
    try {
      const body: any = { client_id: Number(id) };
      if (invoiceForm.job_id) body.job_id = Number(invoiceForm.job_id);
      if (invoiceForm.notes) body.notes = invoiceForm.notes;
      if (invoiceForm.due_date) body.due_date = invoiceForm.due_date;
      const result = await api.post("/invoices", body);
      setShowInvoiceCreate(false);
      setInvoiceForm({ job_id: "", notes: "", due_date: "" });
      if (result.id) {
        navigate(`#/invoices/${result.id}`);
      } else {
        load();
      }
    } catch (e: any) {
      setError(e.message);
    }
  }

  if (error) return <div className="error-msg">{error}</div>;
  if (!client) {
    return (
      <div className="loading">
        <div className="loading-spinner" />
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="breadcrumbs">
        <a href="#" onClick={() => navigate("#/clients")}>
          Clients
        </a>
        <span className="separator">&gt;</span>
        <span className="current">
          {client.first_name} {client.last_name}
        </span>
      </div>

      <div className="page-header">
        <h1>
          {client.first_name} {client.last_name}
        </h1>
        <div className="btn-group">
          <button
            className="btn btn-secondary"
            onClick={() => setEditing(!editing)}
          >
            {editing ? "Cancel" : "Edit"}
          </button>
          <button
            className="btn btn-danger"
            onClick={() => setConfirmDelete(true)}
          >
            Delete
          </button>
        </div>
      </div>

      <div className="card">
        {editing ? (
          <>
            <div className="inline-form">
              <div className="form-group">
                <label className="form-label">First Name</label>
                <input
                  type="text"
                  value={editForm.first_name}
                  onChange={(e) =>
                    setEditForm({ ...editForm, first_name: e.target.value })
                  }
                />
              </div>
              <div className="form-group">
                <label className="form-label">Last Name</label>
                <input
                  type="text"
                  value={editForm.last_name}
                  onChange={(e) =>
                    setEditForm({ ...editForm, last_name: e.target.value })
                  }
                />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) =>
                    setEditForm({ ...editForm, email: e.target.value })
                  }
                />
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input
                  type="tel"
                  value={editForm.phone}
                  onChange={(e) =>
                    setEditForm({ ...editForm, phone: e.target.value })
                  }
                />
              </div>
              <div className="form-group">
                <label className="form-label">Business Name</label>
                <input
                  type="text"
                  value={editForm.business_name}
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      business_name: e.target.value,
                    })
                  }
                />
              </div>
              <div className="form-group">
                <label className="form-label">Address</label>
                <input
                  type="text"
                  value={editForm.address}
                  onChange={(e) =>
                    setEditForm({ ...editForm, address: e.target.value })
                  }
                />
              </div>
            </div>
            <button
              className="btn btn-primary"
              style={{ marginTop: 12 }}
              onClick={saveEdit}
            >
              Save Changes
            </button>
          </>
        ) : (
          <div className="detail-grid">
            <div className="detail-field">
              <div className="field-label">Email</div>
              <div className="field-value">{client.email || "—"}</div>
            </div>
            <div className="detail-field">
              <div className="field-label">Phone</div>
              <div className="field-value">{client.phone || "—"}</div>
            </div>
            <div className="detail-field">
              <div className="field-label">Business</div>
              <div className="field-value">
                {client.business_name || "—"}
              </div>
            </div>
            <div className="detail-field">
              <div className="field-label">Address</div>
              <div className="field-value">{client.address || "—"}</div>
            </div>
            <div className="detail-field">
              <div className="field-label">Client Since</div>
              <div className="field-value">
                {formatDate(client.created_at)}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Jobs */}
      <div className="section-header">
        <h3>Jobs</h3>
        <button
          className="btn btn-primary btn-sm"
          onClick={() => setShowJobForm(!showJobForm)}
        >
          {showJobForm ? "Cancel" : "Create Job"}
        </button>
      </div>

      {showJobForm && (
        <div className="card">
          <div className="inline-form">
            <div className="form-group form-group-full">
              <label className="form-label">Title *</label>
              <input
                type="text"
                value={jobForm.title}
                onChange={(e) =>
                  setJobForm({ ...jobForm, title: e.target.value })
                }
              />
            </div>
            <div className="form-group">
              <label className="form-label">Service Type</label>
              <input
                type="text"
                value={jobForm.service_type}
                onChange={(e) =>
                  setJobForm({ ...jobForm, service_type: e.target.value })
                }
              />
            </div>
            <div className="form-group form-group-full">
              <label className="form-label">Description</label>
              <textarea
                value={jobForm.description}
                onChange={(e) =>
                  setJobForm({ ...jobForm, description: e.target.value })
                }
              />
            </div>
          </div>
          <button
            className="btn btn-primary"
            style={{ marginTop: 12 }}
            onClick={createJob}
          >
            Save Job
          </button>
        </div>
      )}

      {(client.jobs || []).length === 0 ? (
        <div className="empty-state">No jobs yet.</div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Title</th>
                <th>Service</th>
                <th>Status</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {(client.jobs || []).map((j: any) => (
                <tr
                  key={j.id}
                  className="table-row"
                  onClick={() => navigate(`#/jobs/${j.id}`)}
                >
                  <td>{j.title}</td>
                  <td>{j.service_type || "—"}</td>
                  <td>
                    <StatusBadge status={j.status} />
                  </td>
                  <td>{formatDate(j.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Invoices */}
      <div className="section-header">
        <h3>Invoices</h3>
        <button
          className="btn btn-primary btn-sm"
          onClick={() => setShowInvoiceCreate(!showInvoiceCreate)}
        >
          {showInvoiceCreate ? "Cancel" : "Create Invoice"}
        </button>
      </div>

      {showInvoiceCreate && (
        <div className="card">
          <div className="inline-form">
            <div className="form-group">
              <label className="form-label">Link to Job (optional)</label>
              <select
                value={invoiceForm.job_id}
                onChange={(e) =>
                  setInvoiceForm({ ...invoiceForm, job_id: e.target.value })
                }
              >
                <option value="">No linked job</option>
                {(client.jobs || []).map((j: any) => (
                  <option key={j.id} value={j.id}>
                    {j.title}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Due Date</label>
              <input
                type="date"
                value={invoiceForm.due_date}
                onChange={(e) =>
                  setInvoiceForm({
                    ...invoiceForm,
                    due_date: e.target.value,
                  })
                }
              />
            </div>
            <div className="form-group form-group-full">
              <label className="form-label">Notes</label>
              <textarea
                value={invoiceForm.notes}
                onChange={(e) =>
                  setInvoiceForm({ ...invoiceForm, notes: e.target.value })
                }
              />
            </div>
          </div>
          <button
            className="btn btn-primary"
            style={{ marginTop: 12 }}
            onClick={createInvoice}
          >
            Create Invoice
          </button>
        </div>
      )}

      {(client.invoices || []).length === 0 ? (
        <div className="empty-state">No invoices yet.</div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Status</th>
                <th>Total</th>
                <th>Due Date</th>
              </tr>
            </thead>
            <tbody>
              {(client.invoices || []).map((inv: any) => (
                <tr
                  key={inv.id}
                  className="table-row"
                  onClick={() => navigate(`#/invoices/${inv.id}`)}
                >
                  <td>{inv.invoice_number || inv.id}</td>
                  <td>
                    <StatusBadge status={inv.status} />
                  </td>
                  <td>{formatCents(inv.total_cents ?? 0)}</td>
                  <td>{inv.due_date ? formatDate(inv.due_date) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Notes */}
      <div className="section-header">
        <h3>Notes</h3>
      </div>
      <NotesList
        entityType="client"
        entityId={client.id}
        notes={client.notes || []}
        onRefresh={load}
      />

      <div style={{ marginTop: 24 }}>
        <button
          className="btn btn-secondary"
          onClick={() => navigate("#/clients")}
        >
          Back to Clients
        </button>
      </div>

      {confirmDelete && (
        <ConfirmDialog
          message={`Delete client "${client.first_name} ${client.last_name}"? This cannot be undone.`}
          onConfirm={deleteClient}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </div>
  );
}

// ── Jobs List ──────────────────────────────────────────────────────────────────

function JobsList() {
  const [jobs, setJobs] = React.useState<any[]>([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [loading, setLoading] = React.useState(true);
  const perPage = 20;

  React.useEffect(() => {
    setLoading(true);
    api
      .get(`/jobs?page=${page}`)
      .then((r) => {
        setJobs(r.data || []);
        setTotal(r.total || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page]);

  return (
    <div>
      <h1>Jobs</h1>

      {loading ? (
        <div className="loading">
          <div className="loading-spinner" />
          <div>Loading...</div>
        </div>
      ) : jobs.length === 0 ? (
        <div className="empty-state">No jobs found.</div>
      ) : (
        <>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Client</th>
                  <th>Service</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => (
                  <tr
                    key={j.id}
                    className="table-row"
                    onClick={() => navigate(`#/jobs/${j.id}`)}
                  >
                    <td>{j.title}</td>
                    <td>
                      {j.client_first_name || j.first_name || ""}{" "}
                      {j.client_last_name || j.last_name || ""}
                    </td>
                    <td>{j.service_type || "—"}</td>
                    <td>
                      <StatusBadge status={j.status} />
                    </td>
                    <td>{formatDate(j.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={page}
            total={total}
            perPage={perPage}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
}

// ── Job Detail ─────────────────────────────────────────────────────────────────

function JobDetail({ id }: { id: string }) {
  const [job, setJob] = React.useState<any>(null);
  const [error, setError] = React.useState("");
  const [editing, setEditing] = React.useState(false);
  const [editForm, setEditForm] = React.useState<any>({});
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [showInvoiceCreate, setShowInvoiceCreate] = React.useState(false);
  const [invoiceNotes, setInvoiceNotes] = React.useState("");
  const [invoiceDueDate, setInvoiceDueDate] = React.useState("");

  function load() {
    api
      .get(`/jobs/${id}`)
      .then((j) => {
        setJob(j);
        setEditForm({
          title: j.title || "",
          description: j.description || "",
          service_type: j.service_type || "",
        });
      })
      .catch((e) => setError(e.message));
  }

  React.useEffect(load, [id]);

  async function saveEdit() {
    try {
      await api.put(`/jobs/${id}`, editForm);
      setEditing(false);
      load();
    } catch (e: any) {
      setError(e.message);
    }
  }

  async function updateStatus(status: string) {
    try {
      await api.put(`/jobs/${id}`, { status });
      load();
    } catch (e: any) {
      setError(e.message);
    }
  }

  async function deleteJob() {
    try {
      await api.del(`/jobs/${id}`);
      navigate("#/jobs");
    } catch (e: any) {
      setError(e.message);
    }
    setConfirmDelete(false);
  }

  async function createInvoice() {
    try {
      const body: any = {
        client_id: job.client_id,
        job_id: Number(id),
      };
      if (invoiceNotes) body.notes = invoiceNotes;
      if (invoiceDueDate) body.due_date = invoiceDueDate;
      const result = await api.post("/invoices", body);
      setShowInvoiceCreate(false);
      setInvoiceNotes("");
      setInvoiceDueDate("");
      if (result.id) {
        navigate(`#/invoices/${result.id}`);
      } else {
        load();
      }
    } catch (e: any) {
      setError(e.message);
    }
  }

  if (error) return <div className="error-msg">{error}</div>;
  if (!job) {
    return (
      <div className="loading">
        <div className="loading-spinner" />
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="breadcrumbs">
        <a href="#" onClick={() => navigate("#/jobs")}>
          Jobs
        </a>
        <span className="separator">&gt;</span>
        <span className="current">{job.title}</span>
      </div>

      <div className="page-header">
        <h1>{job.title}</h1>
        <div className="btn-group">
          <StatusBadge status={job.status} />
          <button
            className="btn btn-secondary"
            onClick={() => setEditing(!editing)}
          >
            {editing ? "Cancel" : "Edit"}
          </button>
          <button
            className="btn btn-danger"
            onClick={() => setConfirmDelete(true)}
          >
            Delete
          </button>
        </div>
      </div>

      <div className="card">
        {editing ? (
          <>
            <div className="inline-form">
              <div className="form-group form-group-full">
                <label className="form-label">Title</label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) =>
                    setEditForm({ ...editForm, title: e.target.value })
                  }
                />
              </div>
              <div className="form-group">
                <label className="form-label">Service Type</label>
                <input
                  type="text"
                  value={editForm.service_type}
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      service_type: e.target.value,
                    })
                  }
                />
              </div>
              <div className="form-group form-group-full">
                <label className="form-label">Description</label>
                <textarea
                  value={editForm.description}
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      description: e.target.value,
                    })
                  }
                />
              </div>
            </div>
            <button
              className="btn btn-primary"
              style={{ marginTop: 12 }}
              onClick={saveEdit}
            >
              Save Changes
            </button>
          </>
        ) : (
          <>
            <div className="detail-grid">
              <div className="detail-field">
                <div className="field-label">Client</div>
                <div className="field-value">
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`#/clients/${job.client_id}`);
                    }}
                  >
                    {job.client_name || "—"}
                  </a>
                </div>
              </div>
              <div className="detail-field">
                <div className="field-label">Service Type</div>
                <div className="field-value">
                  {job.service_type || "—"}
                </div>
              </div>
              <div className="detail-field">
                <div className="field-label">Created</div>
                <div className="field-value">
                  {formatDate(job.created_at)}
                </div>
              </div>
              {job.completed_at && (
                <div className="detail-field">
                  <div className="field-label">Completed</div>
                  <div className="field-value">
                    {formatDate(job.completed_at)}
                  </div>
                </div>
              )}
            </div>
            {job.description && (
              <div className="detail-field" style={{ marginTop: 12 }}>
                <div className="field-label">Description</div>
                <div
                  className="field-value"
                  style={{ whiteSpace: "pre-wrap" }}
                >
                  {job.description}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="card">
        <h3>Update Status</h3>
        <div className="btn-group">
          <button
            className="btn btn-secondary"
            disabled={job.status === "pending"}
            onClick={() => updateStatus("pending")}
          >
            Mark Pending
          </button>
          <button
            className="btn btn-primary"
            disabled={job.status === "in_progress"}
            onClick={() => updateStatus("in_progress")}
          >
            Mark In Progress
          </button>
          <button
            className="btn btn-success"
            disabled={job.status === "completed"}
            onClick={() => updateStatus("completed")}
          >
            Mark Completed
          </button>
          <button
            className="btn btn-danger"
            disabled={job.status === "cancelled"}
            onClick={() => updateStatus("cancelled")}
          >
            Mark Cancelled
          </button>
        </div>
      </div>

      {/* Invoices */}
      <div className="section-header">
        <h3>Invoices</h3>
        <button
          className="btn btn-primary btn-sm"
          onClick={() => setShowInvoiceCreate(!showInvoiceCreate)}
        >
          {showInvoiceCreate ? "Cancel" : "Create Invoice"}
        </button>
      </div>

      {showInvoiceCreate && (
        <div className="card">
          <div className="inline-form">
            <div className="form-group">
              <label className="form-label">Due Date</label>
              <input
                type="date"
                value={invoiceDueDate}
                onChange={(e) => setInvoiceDueDate(e.target.value)}
              />
            </div>
            <div className="form-group form-group-full">
              <label className="form-label">Notes</label>
              <textarea
                value={invoiceNotes}
                onChange={(e) => setInvoiceNotes(e.target.value)}
              />
            </div>
          </div>
          <button
            className="btn btn-primary"
            style={{ marginTop: 12 }}
            onClick={createInvoice}
          >
            Create Invoice
          </button>
        </div>
      )}

      {(job.invoices || []).length === 0 ? (
        <div className="empty-state">No invoices yet.</div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Status</th>
                <th>Total</th>
                <th>Due Date</th>
              </tr>
            </thead>
            <tbody>
              {(job.invoices || []).map((inv: any) => (
                <tr
                  key={inv.id}
                  className="table-row"
                  onClick={() => navigate(`#/invoices/${inv.id}`)}
                >
                  <td>{inv.invoice_number || inv.id}</td>
                  <td>
                    <StatusBadge status={inv.status} />
                  </td>
                  <td>{formatCents(inv.total_cents ?? 0)}</td>
                  <td>{inv.due_date ? formatDate(inv.due_date) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Notes */}
      <div className="section-header">
        <h3>Notes</h3>
      </div>
      <NotesList
        entityType="job"
        entityId={job.id}
        notes={job.notes || []}
        onRefresh={load}
      />

      <div style={{ marginTop: 24 }}>
        <button
          className="btn btn-secondary"
          onClick={() => navigate("#/jobs")}
        >
          Back to Jobs
        </button>
      </div>

      {confirmDelete && (
        <ConfirmDialog
          message={`Delete job "${job.title}"? This cannot be undone.`}
          onConfirm={deleteJob}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </div>
  );
}

// ── Invoices List ──────────────────────────────────────────────────────────────

function InvoicesList() {
  const [invoices, setInvoices] = React.useState<any[]>([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [loading, setLoading] = React.useState(true);
  const perPage = 20;

  React.useEffect(() => {
    setLoading(true);
    api
      .get(`/invoices?page=${page}`)
      .then((r) => {
        setInvoices(r.data || []);
        setTotal(r.total || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page]);

  return (
    <div>
      <h1>Invoices</h1>

      {loading ? (
        <div className="loading">
          <div className="loading-spinner" />
          <div>Loading...</div>
        </div>
      ) : invoices.length === 0 ? (
        <div className="empty-state">No invoices found.</div>
      ) : (
        <>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Client</th>
                  <th>Status</th>
                  <th>Total</th>
                  <th>Due Date</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr
                    key={inv.id}
                    className="table-row"
                    onClick={() =>
                      navigate(`#/invoices/${inv.id}`)
                    }
                  >
                    <td>{inv.invoice_number || inv.id}</td>
                    <td>
                      {inv.client_first_name || inv.first_name || ""}{" "}
                      {inv.client_last_name || inv.last_name || ""}
                    </td>
                    <td>
                      <StatusBadge status={inv.status} />
                    </td>
                    <td>{formatCents(inv.total_cents ?? 0)}</td>
                    <td>
                      {inv.due_date ? formatDate(inv.due_date) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={page}
            total={total}
            perPage={perPage}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
}

// ── Invoice Detail ─────────────────────────────────────────────────────────────

interface LineItem {
  description: string;
  quantity: number;
  unit_price_cents: number;
}

function InvoiceDetail({ id }: { id: string }) {
  const [invoice, setInvoice] = React.useState<any>(null);
  const [error, setError] = React.useState("");
  const [lineItems, setLineItems] = React.useState<LineItem[]>([]);
  const [taxRateBps, setTaxRateBps] = React.useState(775);
  const [notes, setNotes] = React.useState("");
  const [dueDate, setDueDate] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [confirmDelete, setConfirmDelete] = React.useState(false);

  function load() {
    api
      .get(`/invoices/${id}`)
      .then((inv) => {
        setInvoice(inv);
        const items: LineItem[] = inv.line_items_json
          ? typeof inv.line_items_json === "string"
            ? JSON.parse(inv.line_items_json)
            : inv.line_items_json
          : [];
        setLineItems(
          items.length > 0
            ? items
            : [{ description: "", quantity: 1, unit_price_cents: 0 }]
        );
        setTaxRateBps(inv.tax_rate_bps ?? 775);
        setNotes(inv.notes || "");
        setDueDate(inv.due_date || "");
      })
      .catch((e) => setError(e.message));
  }

  React.useEffect(load, [id]);

  function addLineItem() {
    setLineItems([
      ...lineItems,
      { description: "", quantity: 1, unit_price_cents: 0 },
    ]);
  }

  function removeLineItem(idx: number) {
    setLineItems(lineItems.filter((_, i) => i !== idx));
  }

  function updateLineItem(idx: number, field: keyof LineItem, value: string | number) {
    const updated = lineItems.map((item, i) =>
      i === idx ? { ...item, [field]: value } as LineItem : item
    );
    setLineItems(updated);
  }

  const subtotalCents = lineItems.reduce(
    (sum, item) => sum + item.quantity * item.unit_price_cents,
    0
  );
  const taxRate = taxRateBps / 10000;
  const taxCents = Math.round(subtotalCents * taxRate);
  const totalCents = subtotalCents + taxCents;

  async function saveInvoice() {
    setSaving(true);
    try {
      await api.put(`/invoices/${id}`, {
        line_items_json: lineItems,
        notes,
        due_date: dueDate || null,
        tax_rate_bps: taxRateBps,
      });
      load();
    } catch (e: any) {
      setError(e.message);
    }
    setSaving(false);
  }

  async function sendInvoice() {
    setSaving(true);
    try {
      await api.post(`/invoices/${id}/send`);
      load();
    } catch (e: any) {
      setError(e.message);
    }
    setSaving(false);
  }

  async function markPaid() {
    setSaving(true);
    try {
      await api.put(`/invoices/${id}`, { status: "paid" });
      load();
    } catch (e: any) {
      setError(e.message);
    }
    setSaving(false);
  }

  async function markOverdue() {
    setSaving(true);
    try {
      await api.put(`/invoices/${id}`, { status: "overdue" });
      load();
    } catch (e: any) {
      setError(e.message);
    }
    setSaving(false);
  }

  async function deleteInvoice() {
    try {
      await api.del(`/invoices/${id}`);
      navigate("#/invoices");
    } catch (e: any) {
      setError(e.message);
    }
    setConfirmDelete(false);
  }

  if (error) return <div className="error-msg">{error}</div>;
  if (!invoice) {
    return (
      <div className="loading">
        <div className="loading-spinner" />
        <div>Loading...</div>
      </div>
    );
  }

  const invoiceNumber = invoice.invoice_number || invoice.id;
  const isDraft = invoice.status === "draft";
  const isSent = invoice.status === "sent";
  const isPaid = invoice.status === "paid";
  const isOverdue = invoice.status === "overdue";

  return (
    <div>
      <div className="breadcrumbs">
        <a href="#" onClick={() => navigate("#/invoices")}>
          Invoices
        </a>
        <span className="separator">&gt;</span>
        <span className="current">Invoice #{invoiceNumber}</span>
      </div>

      <div className="page-header">
        <h1>Invoice #{invoiceNumber}</h1>
        <div className="btn-group">
          <StatusBadge status={invoice.status} />
          {invoice.public_token && (
            <button
              className="btn btn-secondary"
              onClick={() =>
                window.open(`/invoice/${invoice.public_token}`, "_blank")
              }
            >
              View Public Link
            </button>
          )}
        </div>
      </div>

      {/* Client info */}
      <div className="card">
        <div className="detail-grid">
          <div className="detail-field">
            <div className="field-label">Client</div>
            <div className="field-value">
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  navigate(`#/clients/${invoice.client_id}`);
                }}
              >
                {invoice.client_first_name || invoice.first_name || ""}{" "}
                {invoice.client_last_name || invoice.last_name || ""}
              </a>
            </div>
          </div>
          <div className="detail-field">
            <div className="field-label">Email</div>
            <div className="field-value">
              {invoice.client_email || invoice.email || "—"}
            </div>
          </div>
          {invoice.job_id && (
            <div className="detail-field">
              <div className="field-label">Linked Job</div>
              <div className="field-value">
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`#/jobs/${invoice.job_id}`);
                  }}
                >
                  {invoice.job_title || `Job #${invoice.job_id}`}
                </a>
              </div>
            </div>
          )}
          <div className="detail-field">
            <div className="field-label">Created</div>
            <div className="field-value">
              {formatDate(invoice.created_at)}
            </div>
          </div>
          {isPaid && invoice.paid_at && (
            <div className="detail-field">
              <div className="field-label">Paid</div>
              <div className="field-value">
                {formatDate(invoice.paid_at)}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Line Items Editor */}
      <div className="card">
        <h3>Line Items</h3>

        <div style={{ marginBottom: 8 }}>
          <div
            className="line-item-row"
            style={{ fontWeight: 600, color: "#999", fontSize: 13 }}
          >
            <div className="line-item-desc">DESCRIPTION</div>
            <div className="line-item-qty">QTY</div>
            <div className="line-item-price">UNIT PRICE ($)</div>
            <div className="line-item-total">TOTAL</div>
            <div style={{ minWidth: 44 }}></div>
          </div>
        </div>

        {lineItems.map((item, idx) => (
          <div className="line-item-row" key={idx}>
            <input
              className="line-item-desc"
              type="text"
              placeholder="Description"
              value={item.description}
              onChange={(e) =>
                updateLineItem(idx, "description", e.target.value)
              }
              readOnly={!isDraft}
            />
            <input
              className="line-item-qty"
              type="number"
              min={1}
              value={item.quantity}
              onChange={(e) =>
                updateLineItem(
                  idx,
                  "quantity",
                  Math.max(1, parseInt(e.target.value) || 1)
                )
              }
              readOnly={!isDraft}
            />
            <input
              className="line-item-price"
              type="number"
              min={0}
              step={0.01}
              placeholder="0.00"
              value={(item.unit_price_cents / 100).toFixed(2)}
              onChange={(e) =>
                updateLineItem(
                  idx,
                  "unit_price_cents",
                  Math.max(0, Math.round(parseFloat(e.target.value || "0") * 100))
                )
              }
              readOnly={!isDraft}
            />
            <div className="line-item-total">
              {formatCents(item.quantity * item.unit_price_cents)}
            </div>
            {isDraft && (
              <button
                className="line-item-remove"
                title="Remove"
                onClick={() => removeLineItem(idx)}
              >
                X
              </button>
            )}
          </div>
        ))}

        {isDraft && (
          <button
            className="btn btn-secondary btn-sm"
            style={{ marginTop: 12 }}
            onClick={addLineItem}
          >
            Add Line Item
          </button>
        )}

        <div className="invoice-totals">
          <div className="total-row">
            <span className="total-label">Subtotal</span>
            <span className="total-value">{formatCents(subtotalCents)}</span>
          </div>
          <div className="total-row">
            <span className="total-label">
              Tax (
              {isDraft ? (
                <input
                  type="number"
                  step={0.01}
                  min={0}
                  style={{
                    width: 70,
                    display: "inline",
                    padding: "4px 6px",
                    fontSize: 14,
                    textAlign: "right",
                  }}
                  value={(taxRateBps / 100).toFixed(2)}
                  onChange={(e) => {
                    const pct = parseFloat(e.target.value) || 0;
                    setTaxRateBps(Math.round(pct * 100));
                  }}
                />
              ) : (
                (taxRateBps / 100).toFixed(2)
              )}
              %)
            </span>
            <span className="total-value">{formatCents(taxCents)}</span>
          </div>
          <div className="total-row grand-total">
            <span className="total-label">Total</span>
            <span className="total-value">{formatCents(totalCents)}</span>
          </div>
        </div>
      </div>

      {/* Due Date + Notes */}
      <div className="card">
        <div className="inline-form">
          <div className="form-group">
            <label className="form-label">Due Date</label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              readOnly={!isDraft}
            />
          </div>
        </div>
        <div className="form-group" style={{ marginTop: 12 }}>
          <label className="form-label">Notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            readOnly={!isDraft}
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="btn-group" style={{ marginBottom: 20 }}>
        {isDraft && (
          <>
            <button
              className="btn btn-primary"
              disabled={saving}
              onClick={saveInvoice}
            >
              {saving ? "Saving..." : "Save Invoice"}
            </button>
            <button
              className="btn btn-success"
              disabled={saving}
              onClick={async () => {
                await saveInvoice();
                await sendInvoice();
              }}
            >
              Send Invoice
            </button>
            <button
              className="btn btn-danger"
              onClick={() => setConfirmDelete(true)}
            >
              Delete Invoice
            </button>
          </>
        )}
        {isSent && (
          <>
            <button
              className="btn btn-success"
              disabled={saving}
              onClick={markPaid}
            >
              Mark Paid
            </button>
            <button
              className="btn btn-danger"
              disabled={saving}
              onClick={markOverdue}
            >
              Mark Overdue
            </button>
          </>
        )}
        {isOverdue && (
          <button
            className="btn btn-success"
            disabled={saving}
            onClick={markPaid}
          >
            Mark Paid
          </button>
        )}
        {isPaid && invoice.paid_at && (
          <div style={{ color: "#22c55e", fontSize: 17, fontWeight: 600 }}>
            Paid on {formatDate(invoice.paid_at)}
          </div>
        )}
      </div>

      <button
        className="btn btn-secondary"
        onClick={() => navigate("#/invoices")}
      >
        Back to Invoices
      </button>

      {confirmDelete && (
        <ConfirmDialog
          message="Delete this draft invoice? This cannot be undone."
          onConfirm={deleteInvoice}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </div>
  );
}

// ── Admin Users ────────────────────────────────────────────────────────────────

function AdminUsers({ currentUser }: { currentUser: any }) {
  const [users, setUsers] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [showAdd, setShowAdd] = React.useState(false);
  const [form, setForm] = React.useState({ email: "", name: "" });
  const [error, setError] = React.useState("");
  const [confirmDeleteId, setConfirmDeleteId] = React.useState<
    number | null
  >(null);

  function load() {
    setLoading(true);
    api
      .get("/admin-users")
      .then((data) => setUsers(Array.isArray(data) ? data : data.data || []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }

  React.useEffect(load, []);

  async function addUser() {
    if (!form.email.trim() || !form.name.trim()) return;
    try {
      await api.post("/admin-users", form);
      setForm({ email: "", name: "" });
      setShowAdd(false);
      load();
    } catch (e: any) {
      setError(e.message);
    }
  }

  async function deleteUser(userId: number) {
    try {
      await api.del(`/admin-users/${userId}`);
      load();
    } catch (e: any) {
      setError(e.message);
    }
    setConfirmDeleteId(null);
  }

  return (
    <div>
      <div className="page-header">
        <h1>Admin Users</h1>
        <button
          className="btn btn-primary"
          onClick={() => setShowAdd(!showAdd)}
        >
          {showAdd ? "Cancel" : "Add Admin"}
        </button>
      </div>

      {error && <div className="error-msg">{error}</div>}

      {showAdd && (
        <div className="card">
          <h3>Add Admin User</h3>
          <div className="inline-form">
            <div className="form-group">
              <label className="form-label">Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Email *</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) =>
                  setForm({ ...form, email: e.target.value })
                }
              />
            </div>
          </div>
          <button
            className="btn btn-primary"
            style={{ marginTop: 12 }}
            onClick={addUser}
          >
            Save
          </button>
        </div>
      )}

      {loading ? (
        <div className="loading">
          <div className="loading-spinner" />
          <div>Loading...</div>
        </div>
      ) : users.length === 0 ? (
        <div className="empty-state">No admin users found.</div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Added</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.admin_user_id}>
                  <td>{u.admin_name || u.name || "—"}</td>
                  <td>{u.email}</td>
                  <td>{u.role || "admin"}</td>
                  <td>{formatDate(u.created_at)}</td>
                  <td>
                    {u.admin_user_id !== currentUser?.admin_user_id && (
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() =>
                          setConfirmDeleteId(u.admin_user_id)
                        }
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {confirmDeleteId !== null && (
        <ConfirmDialog
          message="Remove this admin user? They will lose access to the admin panel."
          onConfirm={() => deleteUser(confirmDeleteId)}
          onCancel={() => setConfirmDeleteId(null)}
        />
      )}
    </div>
  );
}

// ── Settings ──────────────────────────────────────────────────────────────────

function Settings() {
  const [emails, setEmails] = React.useState<string[]>([]);
  const [input, setInput] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [dbHasEmails, setDbHasEmails] = React.useState(false);

  React.useEffect(() => {
    api.get("/settings/notification-emails")
      .then((data) => {
        setEmails(data.emails || []);
        setDbHasEmails((data.emails || []).length > 0);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  async function save(updated: string[]) {
    setSaving(true);
    try {
      const data = await api.put("/settings/notification-emails", { emails: updated });
      setEmails(data.emails || []);
      setDbHasEmails((data.emails || []).length > 0);
    } catch {}
    setSaving(false);
  }

  function addEmail() {
    const trimmed = input.trim().toLowerCase();
    if (!trimmed || emails.includes(trimmed)) return;
    save([...emails, trimmed]);
    setInput("");
  }

  function removeEmail(email: string) {
    save(emails.filter((e) => e !== email));
  }

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div>
      <h1>Settings</h1>

      <div className="card">
        <h2>Notification Emails</h2>
        <p style={{ color: "#999", marginBottom: "1rem", fontSize: "0.9rem" }}>
          Contact form submissions are emailed to these addresses.
          {!dbHasEmails && " Currently using CONTACT_EMAIL environment variable as fallback."}
        </p>

        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem" }}>
          <input
            type="email"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addEmail()}
            placeholder="Add email address"
            className="form-input"
            style={{ flex: 1 }}
          />
          <button
            className="btn btn-primary"
            onClick={addEmail}
            disabled={saving || !input.trim()}
          >
            Add
          </button>
        </div>

        {emails.length === 0 ? (
          <p style={{ color: "#666", fontStyle: "italic" }}>
            No emails configured. Falling back to CONTACT_EMAIL env var.
          </p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            {emails.map((email) => (
              <div
                key={email}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "0.5rem 0.75rem",
                  background: "#1a1a2e",
                  borderRadius: "6px",
                  border: "1px solid #333",
                }}
              >
                <span>{email}</span>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => removeEmail(email)}
                  disabled={saving}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── App (Router + Layout) ──────────────────────────────────────────────────────

export function App() {
  const [route, setRoute] = React.useState(parseHash());
  const [user, setUser] = React.useState<any>(null);
  const [authChecked, setAuthChecked] = React.useState(false);
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  // Listen for hash changes
  React.useEffect(() => {
    function onHashChange() {
      setRoute(parseHash());
    }
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  // Auth check
  React.useEffect(() => {
    api
      .get("/me")
      .then((u) => {
        setUser(u);
        setAuthChecked(true);
      })
      .catch(() => {
        // api.ts will redirect to /auth/login on 401
        setAuthChecked(true);
      });
  }, []);

  if (!authChecked) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          background: "#1a1a2e",
        }}
      >
        <div className="loading">
          <div className="loading-spinner" />
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  const navItems = [
    { hash: "#/dashboard", label: "Dashboard", icon: "~" },
    { hash: "#/submissions", label: "Submissions", icon: ">" },
    { hash: "#/clients", label: "Clients", icon: "*" },
    { hash: "#/jobs", label: "Jobs", icon: "#" },
    { hash: "#/invoices", label: "Invoices", icon: "$" },
    { hash: "#/admin-users", label: "Admin Users", icon: "@" },
    { hash: "#/settings", label: "Settings", icon: "%" },
  ];

  function isActive(hash: string) {
    const target = hash.replace("#/", "");
    return route.page === target;
  }

  function renderPage() {
    switch (route.page) {
      case "dashboard":
        return <Dashboard />;
      case "submissions":
        return route.id ? (
          <SubmissionDetail id={route.id} />
        ) : (
          <SubmissionsList />
        );
      case "clients":
        return route.id ? (
          <ClientDetail id={route.id} />
        ) : (
          <ClientsList />
        );
      case "jobs":
        return route.id ? (
          <JobDetail id={route.id} />
        ) : (
          <JobsList />
        );
      case "invoices":
        return route.id ? (
          <InvoiceDetail id={route.id} />
        ) : (
          <InvoicesList />
        );
      case "admin-users":
        return <AdminUsers currentUser={user} />;
      case "settings":
        return <Settings />;
      default:
        return <Dashboard />;
    }
  }

  return (
    <>
      <button
        className="mobile-menu-btn"
        onClick={() => setSidebarOpen((o) => !o)}
        aria-label="Toggle menu"
      >
        {sidebarOpen ? "\u2715" : "\u2630"}
      </button>
      <div
        className={`sidebar-overlay${sidebarOpen ? " visible" : ""}`}
        onClick={() => setSidebarOpen(false)}
      />
      <nav className={`sidebar${sidebarOpen ? " open" : ""}`}>
        <div className="sidebar-title">Desert Arcade Admin</div>
        {navItems.map((item) => (
          <button
            key={item.hash}
            className={`sidebar-link ${isActive(item.hash) ? "active" : ""}`}
            onClick={() => {
              navigate(item.hash);
              setSidebarOpen(false);
            }}
          >
            <span>{item.icon}</span>
            <span>{item.label}</span>
          </button>
        ))}
        <div className="sidebar-spacer" />
        {user && (
          <div className="sidebar-user">
            <strong>{user.admin_name || user.name || user.email}</strong>
            {user.email}
          </div>
        )}
      </nav>
      <main className="main-content">{renderPage()}</main>
    </>
  );
}
